import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../styles/camera.scss";

export type SnapshotFormat = "jpg" | "png" | "webp";
export type CameraState = "initializing" | "disabled" | "activating" | "enabled" | "error";

const MIME_TYPES: Record<SnapshotFormat, string> = {
	jpg: "image/jpeg",
	png: "image/png",
	webp: "image/webp",
};

export type ZoomCaps = {
	min?: number;
	max?: number;
};

type CameraProps = {
	enabled?: boolean;
	zoomLevel?: number;
	width?: number | null;
	height?: number | null;
	streamWidth?: number | null;
	streamHeight?: number | null;
	requestSnapshot?: boolean;
	snapshotFormat?: SnapshotFormat;
	device?: string | null;
	onStateChanged?: (state: CameraState) => void;
	onWarningRaised?: (message: string) => void;
	onErrorRaised?: (message: string) => void;
	onZoomLevelChanged?: (zoomLevel: number) => void;
	onZoomLevelsSupportedChanged?: (caps: ZoomCaps) => void;
	onSnapshotReady?: (imageData: any) => void;
	onDevicesAvailable?: (devices: MediaDeviceInfo[]) => void;
	onDeviceChanged?: (device: MediaDeviceInfo) => void;
};

const Camera: React.FunctionComponent<CameraProps> = ({
	enabled = false,
	zoomLevel = 1,
	width = null,
	height = null,
	streamWidth = 2560,
	streamHeight = 1440,
	requestSnapshot = false,
	snapshotFormat = "jpg",
	device = null,
	onStateChanged,
	onWarningRaised,
	onErrorRaised,
	onZoomLevelChanged,
	onZoomLevelsSupportedChanged,
	onSnapshotReady,
	onDevicesAvailable,
	onDeviceChanged
}) => {
	const [cameraState, setCameraStateRaw] = useState<CameraState>("initializing");
	const [snapshotQueued, setSnapshotQueued] = useState(false);
	const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
	const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
	const [zoomCaps, setZoomCaps] = useState<ZoomCaps>({ min: undefined, max: undefined });

	// Incremented each time the stream should (re)start; allows re-triggering from same state.
	const [activationCount, setActivationCount] = useState(0);

	const videoRef = useRef<HTMLVideoElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const streamRef = useRef<MediaStream | null>(null);

	// Refs that mirror state/props so async callbacks always read the current value.
	const cameraStateRef = useRef<CameraState>("initializing");
	const selectedDeviceRef = useRef<string | null>(null);
	const initializedRef = useRef(false);
	const prevEnabledRef = useRef<boolean>(enabled);
	const prevSnapshotRef = useRef<boolean>(false); // Always start false to detect initial true.
	const enabledRef = useRef<boolean>(enabled);
	const zoomLevelRef = useRef(zoomLevel);
	const streamWidthRef = useRef(streamWidth);
	const streamHeightRef = useRef(streamHeight);
	const snapshotFormatRef = useRef(snapshotFormat);
	const onStateChangedRef = useRef(onStateChanged);
	const onWarningRaisedRef = useRef(onWarningRaised);
	const onErrorRaisedRef = useRef(onErrorRaised);
	const onZoomLevelChangedRef = useRef(onZoomLevelChanged);
	const onZoomLevelsSupportedChangedRef = useRef(onZoomLevelsSupportedChanged);
	const onSnapshotReadyRef = useRef(onSnapshotReady);
	const onDevicesAvailableRef = useRef(onDevicesAvailable);
	const onDeviceChangedRef = useRef(onDeviceChanged);

	// Keep all prop refs current on every render.
	zoomLevelRef.current = zoomLevel;
	enabledRef.current = enabled;
	streamWidthRef.current = streamWidth;
	streamHeightRef.current = streamHeight;
	snapshotFormatRef.current = snapshotFormat;
	onStateChangedRef.current = onStateChanged;
	onWarningRaisedRef.current = onWarningRaised;
	onErrorRaisedRef.current = onErrorRaised;
	onZoomLevelChangedRef.current = onZoomLevelChanged;
	onZoomLevelsSupportedChangedRef.current = onZoomLevelsSupportedChanged;
	onSnapshotReadyRef.current = onSnapshotReady;
	onDevicesAvailableRef.current = onDevicesAvailable;
	onDeviceChangedRef.current = onDeviceChanged;

	// Stable state setter that also updates the ref, fires the event, and triggers activation.
	const setCameraState = useCallback((newState: CameraState) => {
		cameraStateRef.current = newState;
		setCameraStateRaw(newState);
		if (newState === "activating") {
			setActivationCount(c => c + 1);
		}
		onStateChangedRef.current?.(newState);
	}, []);

	const stopStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach(t => t.stop());
			streamRef.current = null;
		}
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}
	}, []);

	const applyZoom = useCallback(async (level: number) => {
		if (!streamRef.current) return;
		const track = streamRef.current.getVideoTracks()[0];
		if (!track) return;
		const caps = track.getCapabilities() as any;
		if (!caps.zoom) {
			onWarningRaisedRef.current?.("This camera does not support zoom.");
			return;
		}
		const { min, max } = caps.zoom;
		if (level < min || level > max) {
			onWarningRaisedRef.current?.(
				`Zoom level ${level} is outside the supported range [${min}, ${max}].`
			);
			return;
		}
		try {
			await (track.applyConstraints as any)({ advanced: [{ zoom: level }] });
			onZoomLevelChangedRef.current?.(level);
		} catch {
			onWarningRaisedRef.current?.(`Failed to apply zoom level ${level}.`);
		}
	}, []);

	// ── Initialization ────────────────────────────────────────────────────────
	useEffect(() => {
		const init = async () => {
			// Probe getUserMedia to trigger the browser permission prompt so that
			// subsequent enumerateDevices returns labeled entries.
			const probe = await navigator.mediaDevices
				.getUserMedia({ video: true, audio: false })
				.catch(() => null);
			if (probe) probe.getTracks().forEach(t => t.stop());

			const rawDevices = await navigator.mediaDevices
				.enumerateDevices()
				.catch(() => [] as MediaDeviceInfo[]);
			const videoDevices = rawDevices.filter(d => d.kind === "videoinput");

			setAvailableDevices(videoDevices);
			onDevicesAvailableRef.current?.(videoDevices);

			// Prefer the device prop, then best back-facing camera, then first available.
			let initialDevice: string | null = device ?? null;
			if (!initialDevice && videoDevices.length > 0) {
				const backFacing = videoDevices.find(d =>
					/back|rear|environment/i.test(d.label)
				);
				initialDevice = (backFacing ?? videoDevices[0]).deviceId;
			}

			selectedDeviceRef.current = initialDevice;
			setSelectedDevice(initialDevice);
			initializedRef.current = true;

			// enabled / device captured from closure at mount time - intentional.
			if (enabledRef.current) {
				setCameraState("activating");
			} else {
				setCameraState("disabled");
			}
		};

		init();
	}, []);

	// ── Start stream (fires when activationCount increments) ──────────────────
	useEffect(() => {
		if (activationCount === 0 || cameraStateRef.current !== "activating") return;
		const capturedDeviceId = selectedDeviceRef.current;
		let cancelled = false;

		const run = async () => {
			if (!capturedDeviceId) {
				setCameraState("error");
				onErrorRaisedRef.current?.("No camera device is available.");
				return;
			}

			try {
				const videoConstraints: MediaTrackConstraints = { deviceId: { exact: capturedDeviceId } };
				const reqW = streamWidthRef.current;
				const reqH = streamHeightRef.current;
				if (reqW !== null || reqH !== null) {
					// Constrain only the long axis so the short axis floats to the native aspect ratio.
					const isPortrait = (window.screen?.orientation?.type ?? "").startsWith("portrait")
						|| window.innerHeight > window.innerWidth;
					const maxRequested = Math.max(reqW ?? 0, reqH ?? 0);
					if (isPortrait) {
						videoConstraints.height = { ideal: maxRequested };
					} else {
						videoConstraints.width = { ideal: maxRequested };
					}
				}
				const stream = await navigator.mediaDevices.getUserMedia({
					video: videoConstraints,
					audio: false,
				});

				// Bail out if state changed or device was swapped while awaiting.
				if (cancelled || cameraStateRef.current !== "activating" || selectedDeviceRef.current !== capturedDeviceId) {
					stream.getTracks().forEach(t => t.stop());
					return;
				}

				streamRef.current = stream;
				if (videoRef.current) videoRef.current.srcObject = stream;

				const track = stream.getVideoTracks()[0];

				// Upgrade to hardware max on the long axis only; short axis floats to native aspect ratio.
				if (track) {
					const caps = track.getCapabilities() as any;
					const capsW: number | undefined = caps?.width?.max;
					const capsH: number | undefined = caps?.height?.max;
					if (capsW || capsH) {
						const isPortrait = (window.screen?.orientation?.type ?? "").startsWith("portrait")
							|| window.innerHeight > window.innerWidth;
						const maxDim = Math.max(capsW ?? 0, capsH ?? 0);
						await (isPortrait
							? track.applyConstraints({ height: { ideal: maxDim } })
							: track.applyConstraints({ width: { ideal: maxDim } })
						).catch(() => {});
					}
				}

				if (cancelled) {
					stream.getTracks().forEach(t => t.stop());
					streamRef.current = null;
					if (videoRef.current) videoRef.current.srcObject = null;
					return;
				}

				setCameraState("enabled");

				if (track) {
					const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
					const capabilities = track.getCapabilities() as any;
					const activeId = track.getSettings().deviceId;
					const activeDevice = devices.find(
						d => d.kind === "videoinput" && d.deviceId === activeId
					);
					if (activeDevice) onDeviceChangedRef.current?.(activeDevice);

					const newCaps: ZoomCaps = {
						min: capabilities?.zoom?.min,
						max: capabilities?.zoom?.max
					};
					setZoomCaps(newCaps);
					onZoomLevelsSupportedChangedRef.current?.(newCaps);
				}
			} catch (err: any) {
				if (!cancelled && cameraStateRef.current === "activating") {
					setCameraState("error");
					onErrorRaisedRef.current?.(`Camera error: ${err?.message ?? "Unknown error"}`);
				}
			}
		};

		run();

		return () => { cancelled = true; };
	}, [activationCount]);

	// ── Stop stream when disabled ─────────────────────────────────────────────
	useEffect(() => {
		if (cameraState === "disabled") {
			stopStream();
		}
	}, [cameraState, stopStream]);

	// ── Respond to enabled prop changes ───────────────────────────────────────
	useEffect(() => {
		// Skip until initialization has completed (it handles the initial state).
		if (!initializedRef.current) {
			prevEnabledRef.current = enabled;
			return;
		}
		const prev = prevEnabledRef.current;
		prevEnabledRef.current = enabled;

		if (!prev && enabled) {
			setCameraState("activating");
		} else if (prev && !enabled) {
			setCameraState("disabled");
		}
	}, [enabled, setCameraState]);

	// ── Queue snapshot on requestSnapshot false → true ────────────────────────
	useEffect(() => {
		const prev = prevSnapshotRef.current;
		prevSnapshotRef.current = requestSnapshot;
		if (!prev && requestSnapshot) {
			setSnapshotQueued(true);
		}
	}, [requestSnapshot]);

	// ── Take snapshot when enabled and one is queued ──────────────────────────
	useEffect(() => {
		if (cameraState !== "enabled" || !snapshotQueued) return;
		const container = containerRef.current;
		const video = videoRef.current;
		if (!container || !video || video.videoWidth === 0) return;

		const rect = container.getBoundingClientRect();
		const cw = rect.width;
		const ch = rect.height;
		if (cw === 0 || ch === 0) return;

		const vw = video.videoWidth;
		const vh = video.videoHeight;

		// Determine the source-space rectangle visible through the cover-fit viewport,
		// then draw that region at native resolution so the saved image has the same
		// proportions as the viewport but at full source quality.
		const displayScale = Math.max(cw / vw, ch / vh);
		const srcW = Math.round(cw / displayScale);
		const srcH = Math.round(ch / displayScale);
		const srcX = Math.round((vw - srcW) / 2);
		const srcY = Math.round((vh - srcH) / 2);

		const canvas = document.createElement("canvas");
		canvas.width = srcW;
		canvas.height = srcH;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

		const mimeType = MIME_TYPES[snapshotFormatRef.current] ?? MIME_TYPES["jpg"];
		// Return the full data URL so the format is self-describing for the parent.
		onSnapshotReadyRef.current?.(canvas.toDataURL(mimeType));
		setSnapshotQueued(false);
	}, [cameraState, snapshotQueued]);

	// ── Apply zoom when level changes or camera becomes enabled ───────────────
	useEffect(() => {
		if (cameraState === "enabled") {
			applyZoom(zoomLevelRef.current);
		}
		// zoomLevel is listed to trigger this effect when it changes; the actual
		// value is read from the ref inside applyZoom for freshness.
	}, [cameraState, zoomLevel, applyZoom]);

	// ── Handle device prop changes ────────────────────────────────────────────
	useEffect(() => {
		if (!initializedRef.current) return;
		const newDevice = device ?? null;
		const prevDevice = selectedDeviceRef.current;
		selectedDeviceRef.current = newDevice;
		setSelectedDevice(newDevice);
		// Only restart if the device actually changed; parent reporting back the active device is not a change.
		if (newDevice !== prevDevice) {
			const state = cameraStateRef.current;
			if (state === "enabled" || state === "activating") {
				stopStream();
				setCameraState("activating");
			}
		}
	}, [device]);

	// ── Restart stream when resolution constraints change ─────────────────────
	useEffect(() => {
		if (!initializedRef.current) return;
		const state = cameraStateRef.current;
		if (state === "enabled" || state === "activating") {
			stopStream();
			setCameraState("activating");
		}
	}, [streamWidth, streamHeight]);

	// ── Re-apply hardware-max resolution on video resize (handles rotation) ───
	useEffect(() => {
		if (cameraState !== "enabled") return;
		const video = videoRef.current;
		if (!video) return;

		const handleResize = () => {
			const track = streamRef.current?.getVideoTracks()[0];
			if (!track || cameraStateRef.current !== "enabled") return;
			const caps = track.getCapabilities() as any;
			const capsW: number | undefined = caps?.width?.max;
			const capsH: number | undefined = caps?.height?.max;
			if (!capsW && !capsH) return;
			const isPortrait = (window.screen?.orientation?.type ?? "").startsWith("portrait")
				|| window.innerHeight > window.innerWidth;
			const maxDim = Math.max(capsW ?? 0, capsH ?? 0);
			(isPortrait
				? track.applyConstraints({ height: { ideal: maxDim } })
				: track.applyConstraints({ width: { ideal: maxDim } })
			).catch(() => {
				onWarningRaisedRef.current?.("Failed to re-apply resolution after orientation change.");
			});
		};

		video.addEventListener("resize", handleResize);
		return () => video.removeEventListener("resize", handleResize);
	}, [cameraState]);

	// ── Cleanup on unmount ────────────────────────────────────────────────────
	useEffect(() => () => stopStream(), [stopStream]);

	// ── Container sizing ──────────────────────────────────────────────────────
	const containerStyle: React.CSSProperties = {};
	if (width !== null) containerStyle.width = width;
	if (height !== null) containerStyle.height = height;

	// Video sizing adapts to which dimensions are constrained.
	const videoStyle: React.CSSProperties =
		width !== null && height !== null
			? { width: "100%", height: "100%", objectFit: "cover" }
			: width !== null
				? { width: "100%", height: "auto" }
				: height !== null
					? { height: "100%", width: "auto" }
					: { width: "auto", height: "auto" };
	videoStyle.display = cameraState === "enabled" ? "block" : "none";

	return (
		<div className="hwi-camera-container" ref={containerRef} style={containerStyle}>
			<video ref={videoRef} autoPlay playsInline muted style={videoStyle} />
		</div>
	);
};

export default Camera;